"use client";

import { getSchemaConfigs } from "@/lib/schema-configs";
import { localizePageSchemas } from "@/lib/schema";
import type { Locale } from "@/lib/i18n";
import type { LocalizedMetaConfig } from "@/lib/meta-configs";

interface PageSchemasProps {
  route: string;
  /** Set by the /ms/ and /zh/ catch-all so WebPage url/inLanguage match the page. */
  locale?: Locale;
  /**
   * The route's meta-configs `localized[locale]` block. Passed in by the
   * (server) catch-all rather than imported here, so the large meta-configs
   * module stays out of this client component's bundle.
   */
  localizedMeta?: LocalizedMetaConfig;
}

/**
 * Renders all JSON-LD structured data for a given route.
 * Reads from the centralized schema-configs.ts.
 *
 * Usage: <PageSchemas route="services/ai-solutions" />
 *
 * Note: dangerouslySetInnerHTML is safe here because all schema data
 * comes from our own static schema-configs.ts — no user input is involved.
 */
export default function PageSchemas({
  route,
  locale,
  localizedMeta,
}: PageSchemasProps) {
  const configs = getSchemaConfigs(route);
  const schemas = locale
    ? localizePageSchemas(configs, locale, localizedMeta)
    : configs;

  if (schemas.length === 0) {
    return null;
  }

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={`${route}-schema-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
